import { useState, useRef, useCallback, useLayoutEffect } from "react";
import { IoCopyOutline } from "react-icons/io5";
import MentionText from "./MentionText";
import Toast from "./Toast";
import { copyText } from "../utils/copyText";

const MAX_LINES = 3;

// Instagram-style long-caption truncation — clips to exactly 3 lines (via
// CSS line-clamp, so it's always 3 real lines regardless of screen width or
// font size, unlike a fixed character count) with a "more" tucked into the
// end of the last visible line, and a "less" to collapse back. Hashtags are
// linked/pill-styled inline by MentionText itself (same as comments), so
// there's no separate hashtag list here.
export default function TruncatedText({ text }) {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const [toast, setToast] = useState("");
  const elRef = useRef(null);
  const observerRef = useRef(null);

  const measure = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    setIsClamped(el.scrollHeight > el.clientHeight + 1);
  }, []);

  // A callback ref (not just an effect) because the clamped span is
  // unmounted while expanded and a brand-new DOM node is created each time
  // "less" collapses it back — an observer wired up in an effect keyed on
  // `text` alone would keep watching that first, since-discarded node
  // forever after the first expand/collapse cycle. This fires on every
  // attach and detach regardless of *why*, so the ResizeObserver (for a
  // width change — a phone rotation, a sidebar toggling) stays correctly
  // wired no matter how many times this toggles.
  const measureRef = useCallback(el => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    elRef.current = el;
    if (!el) return;

    measure();
    observerRef.current = new ResizeObserver(measure);
    observerRef.current.observe(el);
  }, [measure]);

  // A resize only changes the clamped box's WIDTH — its height stays pinned
  // to exactly MAX_LINES by line-clamp regardless of content, so the
  // ResizeObserver above only ever fires on a width change. If `text` itself
  // changes while this same instance stays mounted (the design at this list
  // position gets new data without a full remount), the DOM node doesn't get
  // re-attached and that callback ref never re-fires — this covers that case
  // by re-measuring directly whenever the text changes.
  useLayoutEffect(() => {
    measure();
  }, [text, measure]);

  if (!text) return null;

  function toggleExpanded(e) {
    e.stopPropagation();
    e.preventDefault();
    setExpanded(prev => !prev);
  }

  async function copyCaption(e) {
    e.stopPropagation();
    e.preventDefault();
    setToast((await copyText(text)) ? "Caption copied" : "Couldn't copy caption");
  }

  // Plain spans/icons with only onClick are invisible to keyboard nav and
  // aren't exposed as controls to assistive tech — matches the role="button"
  // convention already used for the same kind of inline control elsewhere
  // (e.g. Comments.jsx's "Cancel reply").
  function onActivateKey(handler) {
    return e => {
      if (e.key === "Enter" || e.key === " ") handler(e);
    };
  }

  if (expanded) {
    return (
      <>
        <MentionText text={text} />
        {isClamped && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Show less"
            onClick={toggleExpanded}
            onKeyDown={onActivateKey(toggleExpanded)}
            style={toggle}
          >
            {" "}less
          </span>
        )}
        {" "}
        <IoCopyOutline
          role="button"
          tabIndex={0}
          onClick={copyCaption}
          onKeyDown={onActivateKey(copyCaption)}
          style={copyIcon}
          aria-label="Copy caption"
        />
        {toast && <Toast text={toast} onClose={() => setToast("")} />}
      </>
    );
  }

  return (
    <span style={wrapper}>
      <span ref={measureRef} style={clampStyle}><MentionText text={text} /></span>
      {isClamped && (
        <span style={moreOverlay}>
          <span
            role="button"
            tabIndex={0}
            aria-label="Show more"
            onClick={toggleExpanded}
            onKeyDown={onActivateKey(toggleExpanded)}
            style={toggle}
          >
            … more
          </span>
        </span>
      )}
    </span>
  );
}

const wrapper = {
  position: "relative",
  display: "block",
};

const clampStyle = {
  display: "-webkit-box",
  WebkitLineClamp: MAX_LINES,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const moreOverlay = {
  position: "absolute",
  right: 0,
  bottom: 0,
  paddingLeft: 28,
  background: "linear-gradient(to right, transparent, var(--bg-surface) 45%)",
};

const toggle = {
  color: "var(--text-muted)",
  cursor: "pointer",
  fontWeight: 600,
};

const copyIcon = {
  verticalAlign: -2,
  color: "var(--text-muted)",
  cursor: "pointer",
  fontSize: 14,
};
